import { Component, Input, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { QueuesService, QueueBrief, NowResponse } from '../../core/services/queues.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { SessionService } from '../../core/session.service';
import { ModeDetailsModalComponent } from '../mode-details-modal/mode-details-modal.component';
import { formatInTimeZone } from 'date-fns-tz';
import { concat, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { affluenceLabelKey } from '../../core/utils/affluence-label.util';

@Component({
  selector: 'app-queue-card',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIf, ModeDetailsModalComponent, TranslatePipe],
  templateUrl: './queue-card.component.html',
  styleUrls: ['./queue-card.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QueueCardComponent implements OnInit {
  @Input() queue!: QueueBrief | any;

  private api = inject(QueuesService);
  private timezone = inject(TimezoneService);
  private session = inject(SessionService);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  private initialized = false;

  now?: NowResponse;

  // édition
  draftMinutes = 0;
  selectedCause = '';

  // snapshot « enregistré »
  private lastSavedMinutes = 0;
  private lastSavedOpen = true;

  // UI
  isSaving = false;
  savedFlash = false;
  private savedFlashTimer: any;

  // ACK visuel d'état
  stateAck = false;
  stateAckKind: 'open' | 'closed' | null = null;
  private stateAckTimer: any;

  // bump centre
  ringBump = false;
  private ringTimer: any;

  // toast
  toastVisible = false;
  toastSubtitle = '';
  private toastTimer: any;

  // --------- DIALOG DÉTAILS ----------
  showModeDetails = false;

  // badges (ouverts) + fermé
  private readonly levelBadgeSrc: Record<'Fluide'|'Modérée'|'Contrainte', string> = {
    Fluide:  'assets/Fluide.svg',
    Modérée: 'assets/Modérée.svg',
    Contrainte:   'assets/Contrainte.svg',
  };
  private readonly closedBadgeSrc = encodeURI('assets/Fermé.svg');

  badgeSrc(level: 'Fluide'|'Modérée'|'Contrainte'): string {
    return encodeURI(this.levelBadgeSrc[level]);
  }
  statusBadgeSrc(n: NowResponse): string {
    return this.isOpen() ? this.badgeSrc(this.level(n)) : this.closedBadgeSrc;
  }
  statusBadgeAlt(n: NowResponse): string {
    return this.isOpen() ? this.levelLabel(n) : this.translate.instant('components.queueCard.status.closed');
  }
  levelLabel(n: NowResponse): string {
    const level = this.level(n);
    const key = affluenceLabelKey(level === 'Fluide' ? 'fluide' : level === 'Modérée' ? 'moderee' : 'forte');
    return this.translate.instant(key);
  }

  // anneau
  readonly R = 64;
  readonly C = 2 * Math.PI * this.R;

  ngOnInit() {
    if (!this.queue?.id || this.initialized) return;
    this.initialized = true;
    
    this.session.getNowData(this.queue.id).then(n => {
      this.now = n;
      this.lastSavedMinutes = n?.minutes ?? 0;
      this.lastSavedOpen = n?.open ?? (this.queue?.status === 'OPEN');
      this.draftMinutes = this.lastSavedMinutes;
      this.selectedCause = '';
      this.cdr.detectChanges();
    }).catch(err => console.error('API error:', err));
  }

  /* ---------- états pending ---------- */
  get pendingWaitChange(): boolean {
    return (this.now ? this.draftMinutes !== this.lastSavedMinutes : false);
  }
  get pendingOpenChange(): boolean {
    const currentOpen = this.now?.open ?? this.lastSavedOpen;
    return currentOpen !== this.lastSavedOpen;
  }
  get hasPendingChange(): boolean {
    return this.pendingWaitChange || this.pendingOpenChange;
  }
  get isToggling(): boolean { return this.pendingOpenChange; }

  /* ---------- expose les compteurs pour le template ---------- */
  get totalQrScans(): number {
    const n: any = this.now || {};
    return (n.totalQrScans ?? n.total_qr_scans ?? 0) as number;
  }
  get patientSatisfaction(): number {
    const n: any = this.now || {};
    return (n.patientSatisfaction ?? n.patient_satisfaction ?? 0) as number;
  }

  /* ---------- couleurs/format ---------- */
  level(n: NowResponse): 'Fluide'|'Modérée'|'Contrainte' {
    const low  = n.thresholds?.low ?? 10;
    const high = n.thresholds?.medium ?? n.thresholds?.high ?? 20;
    const m = this.minutesToShow(n);
    if (m <= low)   return 'Fluide';
    if (m <= high) return 'Modérée';
    return 'Contrainte';
  }
  ringColor(level: 'Fluide'|'Modérée'|'Contrainte') {
    if (level === 'Fluide')   return '#179B53';
    if (level === 'Modérée')  return '#F1B54A';
    return '#EF4444';
  }
  private lighten(hex: string, ratio = 0.30): string {
    const c = hex.replace('#','');
    const r = parseInt(c.slice(0,2),16);
    const g = parseInt(c.slice(2,4),16);
    const b = parseInt(c.slice(4,6),16);
    const up = (v:number)=> Math.min(255, Math.round(v + (255 - v) * ratio));
    return `#${up(r).toString(16).padStart(2,'0')}${up(g).toString(16).padStart(2,'0')}${up(b).toString(16).padStart(2,'0')}`;
  }
  private darken(hex: string, ratio = 0.18): string {
    const c = hex.replace('#','');
    const r = parseInt(c.slice(0,2),16);
    const g = parseInt(c.slice(2,4),16);
    const b = parseInt(c.slice(4,6),16);
    const dn = (v:number)=> Math.max(0, Math.round(v * (1 - ratio)));
    return `#${dn(r).toString(16).padStart(2,'0')}${dn(g).toString(16).padStart(2,'0')}${dn(b).toString(16).padStart(2,'0')}`;
  }
  ringStart(level: 'Fluide'|'Modérée'|'Contrainte') { return this.lighten(this.ringColor(level), 0.28); }
  ringOuter(level: 'Fluide'|'Modérée'|'Contrainte') { return this.darken(this.ringColor(level), 0.15); }
  ringInner(level: 'Fluide'|'Modérée'|'Contrainte') { return this.lighten(this.ringColor(level), 0.35); }
  trackColor(level: 'Fluide'|'Modérée'|'Contrainte') {
    if (level === 'Fluide')   return '#E9F4ED';
    if (level === 'Modérée')  return '#FFF1DC';
    return '#FDE6E6';
  }
  minutesColor(level: 'Fluide'|'Modérée'|'Contrainte') {
    if (level === 'Fluide')   return '#14532D';
    if (level === 'Modérée')  return '#5C3B09';
    return '#7F1D1D';
  }

  dashOffsetNorm(n: NowResponse): number { return Math.max(0, 100 - this.progressPct(n)); }
  minutesToShow(n: NowResponse) { return this.draftMinutes ?? n.minutes; }
  maxSlider(n: NowResponse) { return Math.max(60, n.thresholds?.high ?? 60); }
  progressPct(n: NowResponse): number {
    const max = this.maxSlider(n);
    const v   = this.minutesToShow(n);
    return Math.max(0, Math.min(100, (v / max) * 100));
  }

  /* ---------- sauvegarde ---------- */
  saveChanges() {
    if (!this.now) return;

    const wasOpenChange = this.pendingOpenChange;
    const wasWaitChange = this.pendingWaitChange;
    if (!wasOpenChange && !wasWaitChange) return;

    const ops: any[] = [];
    const finalOpen = this.isOpen();
    const nextStatus = finalOpen ? 'OPEN' : 'CLOSED';
    const targetMinutes = this.clamp(this.draftMinutes, 0, this.maxSlider(this.now));

    if (wasOpenChange) ops.push(this.api.setStatus(this.queue.id, nextStatus));
    if (wasWaitChange) ops.push(this.api.setManualWait(this.queue.id, targetMinutes, this.selectedCause || undefined));

    this.isSaving = true;

    concat(...(ops.length ? ops : [of(null)]))
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        error: () => {
          this.now = { ...(this.now as NowResponse), open: this.lastSavedOpen, minutes: this.lastSavedMinutes };
          this.queue = { ...(this.queue || {}), status: this.lastSavedOpen ? 'OPEN' : 'CLOSED' };
          this.showToast('Echec de enregistrement');
        },
        complete: () => {
          if (wasOpenChange) {
            this.lastSavedOpen = finalOpen;
            this.stateAckKind = finalOpen ? 'open' : 'closed';
            this.stateAck = true;
            clearTimeout(this.stateAckTimer);
            this.stateAckTimer = setTimeout(() => {
              this.stateAck = false;
              this.cdr.markForCheck();
            }, 800);
          }
          if (wasWaitChange) {
            this.lastSavedMinutes = targetMinutes;
            this.selectedCause = '';
            this.savedFlash = true;
            this.pulseRingCenter();
            clearTimeout(this.savedFlashTimer);
            this.savedFlashTimer = setTimeout(() => {
              this.savedFlash = false;
              this.cdr.markForCheck();
            }, 900);
          }

          this.refreshNow(false);

          if (wasOpenChange && wasWaitChange) this.showToast(`Statut ${finalOpen ? 'Ouvert' : 'Ferme'} + attente ${targetMinutes} min enregistres`);
          else if (wasOpenChange) this.showToast(`Statut ${finalOpen ? 'Ouvert' : 'Ferme'} enregistre`);
          else this.showToast(`Attente mise a ${targetMinutes} min`);
        }
      });
  }

  /* ---------- molette ---------- */
  onCardWheel(evt: WheelEvent) {
    if (this.showModeDetails) return;
    if (!this.isOpen()) return;
    const n = this.now;
    if (!n) return;
    evt.preventDefault(); evt.stopPropagation();
    const dir = evt.deltaY < 0 ? 1 : -1;
    const step = evt.ctrlKey || evt.metaKey ? 10 : (evt.shiftKey ? 5 : 1);
    this.draftMinutes = this.clamp(this.draftMinutes + dir * step, 0, this.maxSlider(n));
    clearTimeout(this.stateAckTimer);
    clearTimeout(this.savedFlashTimer);
    clearTimeout(this.ringTimer);
    this.stateAck = false;
    this.savedFlash = false;
    this.ringBump = false;
  }
  private clamp(v:number,min:number,max:number){ return Math.max(min, Math.min(max, v)); }

  /* ---------- toggle local ---------- */
  isOpen(): boolean { return this.now?.open ?? (this.queue?.status === 'OPEN'); }
  toggleOpen(checked: boolean) {
    if (!this.queue) return;
    const next = checked ? 'OPEN' : 'CLOSED';
    this.now   = { ...(this.now || {} as any), open: checked } as NowResponse;
    this.queue = { ...(this.queue || {}), status: next };
    clearTimeout(this.stateAckTimer);
    clearTimeout(this.savedFlashTimer);
    clearTimeout(this.ringTimer);
    this.stateAck = false;
    this.savedFlash = false;
    this.ringBump = false;
    this.showToast('Modification en attente - cliquer Valider pour enregistrer');
  }

  formatLastUpdate(lastUpdate: string): string {
    if (!lastUpdate) return '---';
    const apiTz = (this.now as any)?.timezone;
    const fallbackTz = this.timezone.getEstablishmentTimezone();
    const tz = apiTz || fallbackTz;
    return formatInTimeZone(lastUpdate, tz, "HH'h'mm");
  }

  // ---------- DETAILS (dialog + contenu) ----------
  openModeDetails() {
    this.showModeDetails = true;
  }
  closeModeDetails() {
    this.showModeDetails = false;
  }

  /* ---------- Accessibilité du bouton mode ---------- */
  modeAria(n: NowResponse): string {
    const mode = n.source === 'MANUAL' ? 'Manuel' : n.source === 'SENSOR' ? 'Capteur' : 'Auto';
    const conf = this.confLabel((n as any).confidence_level);
    return `${mode} - ${conf}`;
  }

  /* ===== Confiance (badge & popup) ===== */
  confLabel(level?: number){ return ({3:'Confiance elevee',2:'Confiance moyenne',1:'Confiance prudente'} as any)[level??0] ?? 'Confiance inconnue'; }

  /* ===== Refresh centralisé (utilisé après toute modif) ===== */
  private refreshNow(silent = true){
    if (!this.queue?.id) return;
    this.session.getNowData(this.queue.id).then(n => {
      this.now = n;
      this.lastSavedMinutes = n?.minutes ?? this.lastSavedMinutes;
      this.lastSavedOpen = n?.open ?? this.lastSavedOpen;
      if (!silent) this.pulseRingCenter();
      this.cdr.detectChanges();
    }).catch(err => console.error('Refresh error:', err));
  }

  private pulseRingCenter() {
    this.ringBump = true;
    clearTimeout(this.ringTimer);
    this.ringTimer = setTimeout(() => {
      this.ringBump = false;
      this.cdr.markForCheck();
    }, 380);
  }

  private showToast(sub: string) {
    this.toastSubtitle = sub;
    this.toastVisible = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.cdr.markForCheck();
    }, 2200);
  }

  cancelChanges() {
    if (!this.now) return;
    this.draftMinutes = this.lastSavedMinutes;
    this.selectedCause = '';
    this.now = { ...this.now, open: this.lastSavedOpen };
    this.queue = { ...(this.queue || {}), status: this.lastSavedOpen ? 'OPEN' : 'CLOSED' };
    this.cdr.markForCheck();
  }
}